import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Music2, Linkedin, Youtube, ArrowUpRight } from "lucide-react";
import Scene3D from "../components/Scene3D";
import ValueCard from "../components/ValueCard";
import StoriesSection from "../components/StoriesSection";
import JoinAgencyButton from "../components/JoinAgencyButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALUES = [
  {
    key: "authenticity",
    title: "Authenticity",
    description: "We help creators stay true to themselves while building their audience.",
  },
  {
    key: "integrity",
    title: "Integrity",
    description: "Trust is the foundation of every partnership.",
  },
  {
    key: "growth",
    title: "Growth",
    description: "Every day is an opportunity to improve.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, margin: "-60px" },
};

const PHILOSOPHY = [
  { step: "Grow", text: "Grow your audience, reach and views with intention." },
  { step: "Engage", text: "Turn viewers into a loyal, active community." },
  { step: "Brand", text: "Develop a personal brand the world remembers." },
  { step: "Connect", text: "Open doors to collaborations and partnerships." },
];

const PILLARS = [
  { key: "audience-growth", name: "Audience Growth", tagline: "Grow your audience with intention." },
  { key: "community-engagement", name: "Community & Engagement", tagline: "Turn viewers into a community." },
  { key: "personal-brand", name: "Personal Brand Development", tagline: "Build a brand the world remembers." },
  { key: "influencer-networking", name: "Influencer Networking & Events", tagline: "Connect with the right people & rooms." },
];

export default function Home() {
  const [openCard, setOpenCard] = useState("authenticity");
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.interest || !form.message) {
      toast.error("Please complete every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Received. We will be in touch soon.");
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain relative min-h-screen w-full overflow-x-hidden bg-[#0a0a0a] text-white">
      {/* NAV */}
      <nav className="fixed top-0 left-0 z-40 flex w-full items-center justify-between px-6 py-6 md:px-12">
        <span className="font-serif-lux text-lg tracking-[0.25em] text-white" data-testid="nav-brand">
          AMANI
        </span>
        <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.25em] text-white/60 md:gap-8">
          <Link to="/services" className="transition-colors hover:text-white" data-testid="nav-services">
            Services
          </Link>
          <Link to="/booking" className="transition-colors hover:text-white" data-testid="nav-booking">
            Booking
          </Link>
          <a href="#values" className="hidden transition-colors hover:text-white sm:inline" data-testid="nav-values">
            Values
          </a>
          <a href="#contact" className="transition-colors hover:text-white" data-testid="nav-contact">
            Contact
          </a>
          <JoinAgencyButton
            testid="nav-join-button"
            label="Join"
            className="rounded-full border border-white/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-white hover:text-black"
          />
        </div>
      </nav>

      {/* HERO */}
      <section className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Scene3D />
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background:
              "radial-gradient(ellipse 60% 45% at 50% 38%, rgba(10,10,10,0) 40%, rgba(10,10,10,0.6) 100%), linear-gradient(to bottom, rgba(10,10,10,0) 55%, rgba(10,10,10,0.95) 82%)",
          }}
        />
        <div className="pointer-events-none absolute bottom-[13%] left-0 z-20 flex w-full flex-col items-center px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mb-5 text-[10px] uppercase tracking-[0.5em] text-white/50"
            data-testid="hero-overline"
          >
            Amani Talent Agency
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-lux text-5xl font-light leading-[0.95] tracking-tight sm:text-6xl md:text-7xl"
            style={{ textShadow: "0 2px 50px rgba(0,0,0,0.95)" }}
            data-testid="hero-tagline"
          >
            Your Potential.
            <br />
            <span className="italic text-white/75">Our Purpose.</span>
          </motion.h1>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-10 left-1/2 z-20 -translate-x-1/2 text-[10px] uppercase tracking-[0.4em] text-white/40"
        >
          Scroll
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-4xl px-6 py-28 text-center md:py-40">
        <motion.p {...fadeUp} transition={{ duration: 1 }} className="mb-8 text-[10px] uppercase tracking-[0.4em] text-white/40">
          Creator Growth Agency
        </motion.p>
        <motion.h2
          {...fadeUp}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-serif-lux text-3xl font-light leading-snug tracking-tight text-white/90 sm:text-4xl md:text-5xl"
        >
          We help influencers grow their audiences, strengthen their personal brands, increase engagement, and connect with the opportunities that take their careers to the next level.
        </motion.h2>
        <motion.p
          {...fadeUp}
          transition={{ duration: 1, delay: 0.25 }}
          className="mx-auto mt-8 max-w-xl text-sm font-light leading-relaxed tracking-wide text-white/50"
        >
          Amani goes beyond the numbers. We build audiences, cultivate communities, develop recognizable brands, and create the relationships that lead to real collaborations and partnerships.
        </motion.p>
        <motion.div {...fadeUp} transition={{ duration: 1, delay: 0.35 }} className="mt-10 flex justify-center">
          <JoinAgencyButton
            testid="intro-join-button"
            className="inline-flex items-center justify-center rounded-full bg-white px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80"
          />
        </motion.div>
      </section>

      {/* STORIES */}
      <StoriesSection />

      {/* PHILOSOPHY */}
      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12 md:pb-40">
        <div className="mb-14 border-b border-white/10 pb-8 text-center">
          <p className="mb-4 text-[10px] uppercase tracking-[0.4em] text-white/40">Our Creator Growth Philosophy</p>
          <h2 className="font-serif-lux text-3xl font-light tracking-tight md:text-5xl">
            Grow. Engage. Brand. Connect.
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {PHILOSOPHY.map((p, i) => (
            <motion.div
              key={p.step}
              data-testid={`philosophy-${p.step.toLowerCase()}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="group relative border border-white/10 bg-white/[0.015] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-white/30"
            >
              <span className="font-serif-lux text-5xl font-light text-white/15 transition-colors group-hover:text-white/30 md:text-6xl">
                0{i + 1}
              </span>
              <h3 className="font-serif-lux mt-4 text-2xl font-light tracking-tight md:text-3xl">{p.step}</h3>
              <p className="mt-3 text-xs font-light leading-relaxed tracking-wide text-white/50">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PILLARS PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12 md:pb-40">
        <div className="mb-14 flex items-end justify-between border-b border-white/10 pb-8">
          <h2 className="font-serif-lux text-4xl font-light tracking-tight md:text-5xl">The Four Pillars</h2>
          <Link to="/services" data-testid="pillars-view-all" className="hidden text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-white sm:block">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Link
              key={p.key}
              to="/services"
              data-testid={`pillar-preview-${p.key}`}
              className="group relative flex min-h-[220px] flex-col justify-between bg-[#0a0a0a] p-8 transition-colors duration-500 hover:bg-white/[0.03]"
            >
              <span className="font-serif-lux text-sm italic text-white/40">0{i + 1}</span>
              <div>
                <h3 className="font-serif-lux text-2xl font-light leading-tight tracking-tight md:text-3xl">{p.name}</h3>
                <p className="mt-3 text-xs font-light leading-relaxed text-white/50">{p.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/40 transition-colors group-hover:text-white">
                  Explore <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section id="values" className="mx-auto max-w-7xl px-6 pb-28 md:px-12 md:pb-40">
        <div className="mb-14 flex items-end justify-between border-b border-white/10 pb-8">
          <h2 className="font-serif-lux text-4xl font-light tracking-tight md:text-5xl">
            Our Core Values
          </h2>
          <span className="hidden text-[10px] uppercase tracking-[0.3em] text-white/40 sm:block">
            Select to explore
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {VALUES.map((v, i) => (
            <ValueCard
              key={v.key}
              value={v}
              index={i}
              isOpen={openCard === v.key}
              onToggle={() => setOpenCard(openCard === v.key ? null : v.key)}
            />
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="border-t border-white/10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 md:grid-cols-2">
          <div className="relative hidden min-h-[600px] overflow-hidden border-r border-white/10 md:flex md:flex-col md:justify-between md:p-14">
            <div
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                background:
                  "radial-gradient(circle at 20% 20%, rgba(255,61,127,0.12), transparent 55%), radial-gradient(circle at 80% 80%, rgba(61,157,255,0.12), transparent 55%)",
              }}
            />
            <p className="relative z-10 text-[10px] uppercase tracking-[0.4em] text-white/40">Amani Talent Agency</p>
            <div className="relative z-10">
              <p className="font-serif-lux text-5xl font-light leading-[0.95] tracking-tight text-white/90 lg:text-6xl">
                Begin<br />your<br /><span className="italic text-white/60">story.</span>
              </p>
              <p className="mt-6 max-w-xs text-sm font-light leading-relaxed text-white/40">
                Grow. Engage. Brand. Connect. Your next chapter starts with a message.
              </p>
            </div>
          </div>

          <div className="px-6 py-20 md:px-14 md:py-28">
            <p className="mb-6 text-[10px] uppercase tracking-[0.4em] text-white/40">Get in touch</p>
            <h2 className="font-serif-lux text-4xl font-light tracking-tight md:text-5xl">
              Let's build something<br />worth remembering.
            </h2>

            <form onSubmit={handleSubmit} className="mt-12 space-y-8" data-testid="contact-form">
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Full Name"
                data-testid="contact-name"
                className="w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white"
              />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email Address"
                data-testid="contact-email"
                className="w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white"
              />
              <select
                name="interest"
                value={form.interest}
                onChange={handleChange}
                data-testid="contact-interest"
                className="w-full cursor-pointer rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white outline-none transition-colors focus:border-white [&>option]:bg-[#0a0a0a]"
              >
                <option value="" disabled>
                  I'm interested in…
                </option>
                <option value="Audience Growth">Audience Growth</option>
                <option value="Community & Engagement">Community &amp; Engagement</option>
                <option value="Personal Brand Development">Personal Brand Development</option>
                <option value="Influencer Networking & Events">Influencer Networking &amp; Events</option>
                <option value="Event Experiences">Event Experiences</option>
              </select>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your goals"
                rows={3}
                data-testid="contact-message"
                className="w-full resize-none rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-3 rounded-none bg-white px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
              >
                {submitting ? "Sending" : "Submit"}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="flex flex-col items-center border-t border-white/10 pt-20 text-center">
        <div className="mb-12 flex items-center gap-10 text-white/50">
          <a href="#" aria-label="Instagram" data-testid="social-instagram" className="transition-colors hover:text-white">
            <Instagram size={18} strokeWidth={1.2} />
          </a>
          <a href="#" aria-label="TikTok" data-testid="social-tiktok" className="transition-colors hover:text-white">
            <Music2 size={18} strokeWidth={1.2} />
          </a>
          <a href="#" aria-label="LinkedIn" data-testid="social-linkedin" className="transition-colors hover:text-white">
            <Linkedin size={18} strokeWidth={1.2} />
          </a>
          <a href="#" aria-label="YouTube" data-testid="social-youtube" className="transition-colors hover:text-white">
            <Youtube size={18} strokeWidth={1.2} />
          </a>
        </div>
        <p className="mb-4 text-[10px] uppercase tracking-[0.4em] text-white/40">
          Your Potential. Our Purpose.
        </p>
        <h2 className="font-serif-lux w-full select-none px-4 text-[16vw] font-light leading-none tracking-tight text-white/90">
          AMANI
        </h2>
        <p className="py-8 text-[10px] uppercase tracking-[0.3em] text-white/30">
          © {new Date().getFullYear()} Amani Talent Agency
        </p>
      </footer>
    </div>
  );
}
